// Registers the service worker (pwa/templates/pwa/sw.js) so the site can be
// installed as an app, and drives the install entry points:
// [data-install-button="<location>"] buttons and the finish modal's
// [data-install-prompt]. Both stay hidden unless this browser can install.

import { onElements } from './dom.js'
import { isEmbedded } from './embed.js'
import { readStorage, writeStorage } from './storage.js'

const BUTTON = '[data-install-button]'
const PROMPT = '[data-install-prompt]'
const DISMISSED_KEY = 'install-prompt-dismissed'
const DISMISS_FOR_MS = 30 * 24 * 60 * 60 * 1000

// Safari on iOS has no install prompt, so iPhone and iPad users get steps
// instead. iPadOS reports itself as a Mac, so it's told apart by touch.
const isIOS =
    /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

// Chrome and Edge's install prompt, held until the visitor asks for it
let installEvent = null

const isInstalled = () =>
    window.matchMedia('(display-mode: standalone)').matches ||
    navigator.standalone === true

const canInstall = () =>
    !isEmbedded && !isInstalled() && (installEvent !== null || isIOS)

const promptDismissed = () =>
    Date.now() - Number(readStorage(DISMISSED_KEY)) < DISMISS_FOR_MS

const track = (event, details = {}) => {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({ event, ...details })
}

const updateButton = (button) => {
    button.hidden = !canInstall()
}

const updatePrompt = (prompt) => {
    prompt.hidden = !canInstall() || promptDismissed()
}

const updateAll = () => {
    document.querySelectorAll(BUTTON).forEach(updateButton)
    document.querySelectorAll(PROMPT).forEach(updatePrompt)
}

const install = async (button) => {
    track('pwa_install_click', {
        install_location: button.dataset.installButton,
    })
    if (!installEvent) {
        // iOS: the prompt shows its steps in place; other buttons open them
        // as a modal via their data-a11y-dialog-show (see initElement)
        const steps = button
            .closest(PROMPT)
            ?.querySelector('[data-install-steps]')
        if (steps) steps.hidden = false
        return
    }
    installEvent.prompt()
    const { outcome } = await installEvent.userChoice
    track('pwa_install_choice', { outcome })
    // Each prompt can only be shown once
    installEvent = null
    updateAll()
}

if ('serviceWorker' in navigator && !isEmbedded) {
    window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js')
    })
}

window.addEventListener('beforeinstallprompt', (event) => {
    // Holds back Chrome's own banner, so install is offered at better
    // moments instead: from the menu and after finishing a game
    event.preventDefault()
    installEvent = event
    updateAll()
})

window.addEventListener('appinstalled', () => {
    installEvent = null
    track('pwa_installed')
    updateAll()
})

const initElement = (element) => {
    if (element.matches(PROMPT)) {
        updatePrompt(element)
        element.closest('[data-dialog]')?.addEventListener('show', () => {
            if (!element.hidden) track('pwa_install_prompt_shown')
        })
        return
    }
    updateButton(element)
    // The shared opener, so e.g. the menu closes itself first (src/js/menu.js).
    // Not inside the prompt, whose modal would cover this one.
    if (isIOS && !element.closest(PROMPT)) {
        element.dataset.a11yDialogShow = 'install-ios-modal'
    }
}

onElements(`${BUTTON}, ${PROMPT}`, initElement)

document.addEventListener('click', (event) => {
    const button = event.target.closest(BUTTON)
    if (button) {
        install(button)
        return
    }
    if (event.target.closest('[data-install-dismiss]')) {
        writeStorage(DISMISSED_KEY, String(Date.now()))
        track('pwa_install_dismissed')
        updateAll()
    }
})
